import React, { useState, useEffect, useRef } from "react";
import Link from "@amzn/meridian/link";
import Icon from "@amzn/meridian/icon";
import chevronLeftSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-left-small";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";

// ScrollableRouteContainer constants
export const LAYOUT_MEASURE_DELAY = 300;
export const TRANSITION_DURATION = 800;
export const ROW_TOLERANCE_PX = 5;
export const ITEM_CONTAINER_SELECTOR = "[data-item-container]";
export const ITEM_SELECTOR = "a";

// Style constants
const BUTTON_COLORS = {
  enabled: "#146eb4",
  disabled: "#b0b0b0",
};

const CONTAINER_SPACING = "8px";

/**
 * Creates default button label with icon
 */
const createDefaultLabel = (iconTokens, text, iconFirst = true) => {
  if (iconFirst) {
    return (
      <React.Fragment>
        <Icon tokens={iconTokens} color="primary" /> {text}
      </React.Fragment>
    );
  }
  return (
    <React.Fragment>
      {text} <Icon tokens={iconTokens} color="primary" />
    </React.Fragment>
  );
};

/**
 * Creates button style based on enabled state
 */
const createButtonStyle = (isEnabled) => ({
  color: isEnabled ? BUTTON_COLORS.enabled : BUTTON_COLORS.disabled,
  pointerEvents: isEnabled ? "auto" : "none",
  cursor: isEnabled ? "pointer" : "default",
  userSelect: "none",
  whiteSpace: "nowrap",
});

/**
 * Reusable pagination button component
 */
const PaginationButton = ({ isEnabled, onClick, children }) => (
  <div style={{ flexShrink: 0 }}>
    <Link
      type="secondary"
      style={createButtonStyle(isEnabled)}
      onClick={onClick}
      disabled={!isEnabled}
    >
      {children}
    </Link>
  </div>
);

/**
 * Flattens React children into processable array
 */
const flattenChildren = (children) => {
  return React.Children.toArray(
    children && children.props && children.props.children
      ? children.props.children
      : children,
  );
};

/**
 * Pagination component that displays items in rows with navigation buttons
 */
const PaginatedRowContainer = ({
  children,
  maxRows = 2,
  leftButtonLabel = "Left",
  rightButtonLabel = "Right",
}) => {
  const containerRef = useRef(null);
  const [itemsPerRow, setItemsPerRow] = useState(0);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const allItems = flattenChildren(children);
  const totalItems = allItems.length;

  /**
   * Measures items per row by comparing DOM positions
   */
  const measureItemsPerRow = () => {
    const itemContainer =
      containerRef.current &&
      containerRef.current.querySelector(ITEM_CONTAINER_SELECTOR);
    const itemElements =
      itemContainer && itemContainer.querySelectorAll(ITEM_SELECTOR);

    if (!itemElements || !itemElements.length) return;

    const firstItemTop = itemElements[0].getBoundingClientRect().top;
    let itemsInFirstRow = 0;

    for (const element of itemElements) {
      const elementTop = element.getBoundingClientRect().top;
      if (Math.abs(elementTop - firstItemTop) <= ROW_TOLERANCE_PX) {
        itemsInFirstRow++;
      } else {
        break;
      }
    }

    if (itemsInFirstRow > 0) {
      setItemsPerRow(itemsInFirstRow);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(measureItemsPerRow, LAYOUT_MEASURE_DELAY);
    const handleResize = () =>
      setTimeout(measureItemsPerRow, LAYOUT_MEASURE_DELAY);

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, [allItems.length]);

  const itemsPerPage = itemsPerRow * maxRows;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const hasPagination = totalItems > itemsPerPage && itemsPerRow > 0;

  /**
   * Handles page transition with animation
   */
  const navigateToPage = (newPageIndex) => {
    if (newPageIndex === currentPageIndex || isTransitioning) return;

    setIsTransitioning(true);
    setCurrentPageIndex(newPageIndex);

    setTimeout(() => setIsTransitioning(false), TRANSITION_DURATION);
  };

  /**
   * Gets items for current page
   */
  const getPageItems = () => {
    if (!hasPagination)
      return allItems.slice(0, itemsPerPage || allItems.length);

    const startIndex = currentPageIndex * itemsPerPage;
    return allItems.slice(startIndex, startIndex + itemsPerPage);
  };

  const canNavigateLeft = hasPagination && currentPageIndex > 0;
  const canNavigateRight = hasPagination && currentPageIndex < totalPages - 1;

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: CONTAINER_SPACING,
        padding: `${CONTAINER_SPACING} 0`,
        width: "100%",
      }}
    >
      <PaginationButton
        isEnabled={canNavigateLeft}
        onClick={() => navigateToPage(currentPageIndex - 1)}
      >
        {React.isValidElement(leftButtonLabel) ? (
          <React.Fragment>
            <Icon tokens={chevronLeftSmallTokens} color="primary" />{" "}
            {leftButtonLabel}
          </React.Fragment>
        ) : (
          createDefaultLabel(chevronLeftSmallTokens, leftButtonLabel, true)
        )}
      </PaginationButton>

      <div
        data-item-container
        style={{
          flex: 1,
          display: "flex",
          flexWrap: "wrap",
          gap: CONTAINER_SPACING,
          alignContent: "flex-start",
          opacity: isTransitioning ? 0.7 : 1,
          transition: `opacity ${TRANSITION_DURATION}ms ease-in-out`,
        }}
      >
        {getPageItems()}
      </div>

      <PaginationButton
        isEnabled={canNavigateRight}
        onClick={() => navigateToPage(currentPageIndex + 1)}
      >
        {React.isValidElement(rightButtonLabel) ? (
          <React.Fragment>
            {rightButtonLabel}{" "}
            <Icon tokens={chevronRightSmallTokens} color="primary" />
          </React.Fragment>
        ) : (
          createDefaultLabel(chevronRightSmallTokens, rightButtonLabel, false)
        )}
      </PaginationButton>
    </div>
  );
};

export default PaginatedRowContainer;



// WEBPACK FOOTER //
// ./src/components/outboundSSD/util/PaginatedRowContainer.js